import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Mail, Phone, ArrowLeft } from "lucide-react";
import fr from "../locales/fr.json";

/**
 * Thank You page is French-only, mounted at /thank_you.
 * It does not consume the I18n context so its language stays fixed.
 */
export default function ThankYou() {
  const data = fr.thank_you;
  const contact = fr.footer.contact;

  return (
    <main data-testid="thank-you-page" className="bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-12">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-700">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            {data.eyebrow}
          </div>
          <h1
            className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl"
            style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
            data-testid="thank-you-title"
          >
            {data.title}
          </h1>
          <p className="mt-4 text-lg text-slate-600">{data.subtitle}</p>

          <div className="mt-10">
            <h2 className="text-xl font-semibold text-slate-900">
              {data.next_steps_title}
            </h2>
            <ul className="mt-4 space-y-3 text-base text-slate-700">
              {data.next_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="text-lg font-semibold text-slate-900">{data.contact_title}</h2>
            <p className="mt-2 text-sm text-slate-600">{data.contact_body}</p>
            <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
              <a
                href={`mailto:${contact.email}`}
                data-testid="thank-you-email-link"
                className="flex items-center gap-2 hover:text-slate-900"
              >
                <Mail className="h-4 w-4 text-amber-700" aria-hidden="true" />
                <span>
                  <span className="text-xs uppercase tracking-widest text-slate-500">
                    {data.email_label}
                  </span>
                  <br />
                  {contact.email}
                </span>
              </a>
              <a
                href={`tel:${contact.phone.replace(/\s+/g, "")}`}
                data-testid="thank-you-phone-link"
                className="flex items-center gap-2 hover:text-slate-900"
              >
                <Phone className="h-4 w-4 text-amber-700" aria-hidden="true" />
                <span>
                  <span className="text-xs uppercase tracking-widest text-slate-500">
                    {data.phone_label}
                  </span>
                  <br />
                  {contact.phone}
                </span>
              </a>
            </div>
          </div>

          <div className="mt-10">
            <Link
              to="/"
              data-testid="thank-you-back-home"
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {data.back_home}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
